import { useAuth } from "@/hooks/use-auth";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { ONBOARDING_RECOMMENDATIONS_FLAG } from "@/lib/feature-flags";
import { toLogin, toOnboarding } from "@/lib/urls";
import { Loader2 } from "lucide-react";
import { Navigate, Outlet, useLocation } from "react-router";

export const AuthenticatedGuard = ({
  requireOnboarding = true,
}: {
  requireOnboarding?: boolean;
}) => {
  const { user, isLoading } = useAuth();
  const [onboardingEnabled] = useFeatureFlag([ONBOARDING_RECOMMENDATIONS_FLAG]);
  const location = useLocation();

  // Only the initial session restore blanks the page. Once there is a user,
  // `isLoading` means something is being refreshed underneath them, and
  // swapping the route out for a spinner unmounts whatever they were doing.
  //
  // That is not hypothetical: updating a password sets `isLoading`, so the
  // onboarding wizard was torn down mid-update. The "advance to the next step"
  // dispatch then landed on a dead component, and the freshly mounted wizard
  // read the step it had stored on the way in — the password step — so setting
  // a password appeared to do nothing at all.
  if (isLoading && !user) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isLoading && !user) {
    return <Navigate to={toLogin(location.pathname)} />;
  }

  // Funnel users who have never completed onboarding (null) into the wizard,
  // preserving where they were headed. `undefined` (older backend, or a
  // non-viewer payload in the cache) must not trigger the funnel. /onboarding
  // itself mounts with requireOnboarding={false}, so no redirect loop.
  if (
    requireOnboarding &&
    onboardingEnabled &&
    user?.onboardingCompletedAt === null
  ) {
    return (
      <Navigate
        to={toOnboarding(location.pathname + location.search)}
        replace
      />
    );
  }

  return <Outlet />;
};
